import { Row, Col } from "react-bootstrap";
import logo from "../../assets/IESA_Logo_25.png";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import CustomButton from "../../components/CustomButton/CustomButton";
import WebService from "../../utility/WebService";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { EyeFill, EyeSlashFill } from 'react-bootstrap-icons';
// import "./login.css";

type FormData = {
    email: string;
    otp: string;
    password: string;
    confirmPassword: string;
};

const ForgotPassword = () => {
    const { register, handleSubmit, watch, trigger, formState: { errors } } = useForm<FormData>();
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [loading, setLoading] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const navigate = useNavigate();

    const email = watch("email");
    const otp = watch("otp");
    const password = watch("password");

    useEffect(() => {
        if (password) {
            trigger("confirmPassword");
        }
    }, [password, trigger]);

    // STEP 1 – Send OTP
    const sendOtp = (data: FormData) => {
        setLoading(true);

        WebService.postAPI({
            action: "users/admin-forgot-password",
            body: { email: data.email },
        })
            .then(() => {
                toast.success("OTP sent to your email");
                setStep(2);
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || "Failed to send OTP");
            })
            .finally(() => setLoading(false));
    };

    // STEP 2 – Verify OTP
    const verifyOtp = () => {
        setLoading(true);

        WebService.postAPI({
            action: "users/verify-admin-otp",
            body: { email, otp },
        })
            .then(() => {
                toast.success("OTP verified");
                setStep(3);
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || "Invalid OTP");
            })
            .finally(() => setLoading(false));
    };

    // STEP 3 – Reset Password
    const resetPassword = (data: FormData) => {
        setLoading(true);

        WebService.postAPI({
            action: "users/reset-admin-password",
            body: {
                email,
                password: data.password,
            },
        })
            .then(() => {
                toast.success("Password reset successfully");
                navigate("/login");
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || "Reset failed");
            })
            .finally(() => setLoading(false));
    };

    return (
        <div className="login-container">
            <Row className="h-100 ms-0 me-0">
                <Col md={12} className="d-flex align-items-center justify-content-center">
                    <div className="login-main">
                        <div className="w-25">

                            {/* Logo */}
                            <div className="d-flex justify-content-center mb-3">
                                <img src={logo} alt="Logo" className="logo" />
                            </div>

                            <div className="sign-in text-center">
                                {step === 1 && "Forgot Password"}
                                {step === 2 && "Verify OTP"}
                                {step === 3 && "Reset Password"}
                            </div>

                            <hr className="line" />

                            {/* STEP 1 */}
                            {step === 1 && (
                                <>
                                    <input
                                        type="email"
                                        placeholder="Enter registered email"
                                        className="input mt-3"
                                        {...register("email", {
                                            required: "Email is required",
                                            pattern: {
                                                value: /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/,
                                                message: "Enter valid email",
                                            },
                                        })}
                                    />
                                    {errors.email && <div className="error-message">{errors.email.message}</div>}

                                    <CustomButton
                                        isLoading={loading}
                                        label="Send OTP"
                                        className="w-100 mt-4"
                                        onClick={handleSubmit(sendOtp)}
                                    />
                                </>
                            )}

                            {/* STEP 2 */}
                            {step === 2 && (
                                <>
                                    <input
                                        placeholder="Enter 6-digit OTP"
                                        className="input mt-3"
                                        {...register("otp", { required: "OTP required" })}
                                    />
                                    {errors.otp && <div className="error-message">{errors.otp.message}</div>}

                                    <CustomButton
                                        isLoading={loading}
                                        label="Verify OTP"
                                        className="w-100 mt-4"
                                        onClick={verifyOtp}
                                    />

                                    <div className="text-center mt-3">
                                        <span
                                            style={{ fontSize: "13px", color: "#0d6efd", cursor: "pointer" }}
                                            onClick={() => setStep(1)}
                                        >
                                            Resend OTP
                                        </span>
                                    </div>
                                </>
                            )}

                            {/* STEP 3 */}
                            {step === 3 && (
                                <>
                                    <div className="position-relative mt-3">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            placeholder="New Password"
                                            className="input pe-5"
                                            style={{
                                                width: "100%",
                                                height: "40px",                 // ✅ fixed height
                                                padding: "12px 44px 12px 12px", // ✅ space for eye icon
                                                border: "1px solid #ccc",
                                                borderRadius: "10px",
                                                fontSize: "14px",
                                                boxSizing: "border-box",        // 🔥 main fix
                                                outline: "none",
                                                marginBottom: "unset"
                                            }}
                                            {...register("password", {
                                                required: "Enter new password",
                                                minLength: {
                                                    value: 6,
                                                    message: "Password must be at least 6 characters"
                                                }
                                            })}
                                        />

                                        <span
                                            // className="password-toggle-icon"
                                            style={{
                                                position: "absolute",
                                                right: "16px",
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                cursor: "pointer"
                                            }}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => setShowPassword(prev => !prev)}
                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                            tabIndex={0}
                                            role="button"
                                        >
                                            {!showPassword ? <EyeFill color="#888" /> : <EyeSlashFill color="#888" />}
                                        </span>
                                    </div>

                                    {errors.password && (
                                        <div className="error-message">{errors.password.message}</div>
                                    )}

                                    <div className="position-relative mt-3">
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            placeholder="Confirm Password"
                                            className="input pe-5"
                                            style={{
                                                width: "100%",
                                                height: "40px",                 // ✅ fixed height
                                                padding: "12px 44px 12px 12px", // ✅ space for eye icon
                                                border: "1px solid #ccc",
                                                borderRadius: "10px",
                                                fontSize: "14px",
                                                boxSizing: "border-box",        // 🔥 main fix
                                                outline: "none",
                                                marginBottom: "unset"
                                            }}
                                            {...register("confirmPassword", {
                                                required: "Confirm your password",
                                                validate: value =>
                                                    value === watch("password") || "Passwords do not match"
                                            })}
                                        />

                                        <span
                                            // className="password-toggle-icon"
                                            style={{
                                                position: "absolute",
                                                right: "16px",
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                cursor: "pointer"
                                            }}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => setShowConfirmPassword(prev => !prev)}
                                            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                            tabIndex={0}
                                            role="button"
                                        >
                                            {!showConfirmPassword ? <EyeFill color="#888" /> : <EyeSlashFill color="#888" />}
                                        </span>
                                    </div>

                                    {errors.confirmPassword && (
                                        <div className="error-message">{errors.confirmPassword.message}</div>
                                    )}

                                    <CustomButton
                                        isLoading={loading}
                                        label="Reset Password"
                                        className="w-100 mt-4"
                                        onClick={handleSubmit(resetPassword)}
                                    />
                                </>
                            )}

                            <div className="text-center mt-3">
                                <span
                                    style={{ fontSize: "13px", color: "#0d6efd", cursor: "pointer" }}
                                    onClick={() => navigate("/login")}
                                >
                                    ← Back to Login
                                </span>
                            </div>

                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default ForgotPassword;
